'use client'

import { X24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Divider, Icon, Spacer, Text } from '@vinted/web-ui'
import {
  QuickRepliesBadge72,
  FastShipperBadge72,
  FrequentUploaderBadge72,
} from '@vinted/multichrome-icons'

import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { INBOX_URL } from '@marketplace-web/messaging/conversation-messaging-data'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  clickEvent,
  SellerBadgeDto,
  SellerBadgeType,
} from '@marketplace-web/seller-engagement-tools/seller-engagement-tools-data'

import {
  MAP_BADGE_TYPE_TO_ACTION_TARGET,
  MAP_BADGE_TYPE_TO_SCREEN,
} from '../../../constants/badges'
import {
  ITEM_UPLOAD_URL,
  MY_ORDERS_SOLD_URL,
  SELLING_HELP_URL,
  TERMS_URL,
} from '../../../constants/routes'

type Props = {
  selectedBadge: SellerBadgeDto | null
  onClose: () => void
}

const BadgeInfoModal = ({ selectedBadge, onClose }: Props) => {
  const translate = useTranslate('profile.badges')
  const { track } = useTracking()

  if (!selectedBadge) return null

  const handleOnClose = () => {
    track(
      clickEvent({
        screen: MAP_BADGE_TYPE_TO_SCREEN[selectedBadge.type],
        target: 'close_badge_info',
      }),
    )
    onClose()
  }

  const handleClick = () => {
    track(
      clickEvent({
        screen: MAP_BADGE_TYPE_TO_SCREEN[selectedBadge.type],
        target: MAP_BADGE_TYPE_TO_ACTION_TARGET[selectedBadge.type],
      }),
    )
  }

  if (!selectedBadge) return null

  const getHeaderText = () => {
    switch (selectedBadge.type) {
      case SellerBadgeType.ActiveLister:
        return translate('frequent_uploads.modal.title')
      case SellerBadgeType.FastReplier:
        return translate('quick_replier.modal.title')
      case SellerBadgeType.SpeedyShipper:
        return translate('speedy_shipping.modal.title')
      default:
        return ''
    }
  }

  const getBodyText = () => {
    switch (selectedBadge.type) {
      case SellerBadgeType.ActiveLister:
        return translate('frequent_uploads.modal.body', {
          items_count: selectedBadge?.progress.goal,
          'catalog-rules-link': chunks => (
            <a href={SELLING_HELP_URL} target="_blank" rel="noopener noreferrer">
              {chunks}
            </a>
          ),
          'terms-and-conditions-link': chunks => (
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
              {chunks}
            </a>
          ),
        })
      case SellerBadgeType.FastReplier:
        return translate('quick_replier.modal.body')
      case SellerBadgeType.SpeedyShipper:
        return translate('speedy_shipping.modal.body')
      default:
        return ''
    }
  }

  const getIconName = () => {
    switch (selectedBadge.type) {
      case SellerBadgeType.FastReplier:
        return QuickRepliesBadge72
      case SellerBadgeType.SpeedyShipper:
        return FastShipperBadge72
      case SellerBadgeType.ActiveLister:
      default:
        return FrequentUploaderBadge72
    }
  }

  const getActionUrl = () => {
    switch (selectedBadge.type) {
      case SellerBadgeType.ActiveLister:
        return ITEM_UPLOAD_URL
      case SellerBadgeType.FastReplier:
        return INBOX_URL
      case SellerBadgeType.SpeedyShipper:
        return MY_ORDERS_SOLD_URL
      default:
        return undefined
    }
  }

  const getActionText = () => {
    switch (selectedBadge.type) {
      case SellerBadgeType.ActiveLister:
        return translate('frequent_uploads.modal.actions.upload_item_now')
      case SellerBadgeType.FastReplier:
        return translate('quick_replier.modal.actions.check_inbox')
      case SellerBadgeType.SpeedyShipper:
        return translate('speedy_shipping.modal.actions.view_orders')
      default:
        return ''
    }
  }

  const renderHeader = () => {
    return (
      <>
        <Cell
          styling={Cell.Styling.Tight}
          prefix={<Spacer orientation={Spacer.Orientation.Horizontal} size={Spacer.Size.X3Large} />}
          suffix={
            <Button
              iconName={X24}
              styling={Button.Styling.Flat}
              onClick={handleOnClose}
              testId="badge-info-modal-close"
            />
          }
        >
          <Spacer size={Spacer.Size.Large} />
          <div className="u-text-center">
            <Text
              as="h2"
              type={Text.Type.Title}
              alignment={Text.Alignment.Center}
              text={getHeaderText()}
            />
          </div>
          <Spacer size={Spacer.Size.Large} />
        </Cell>
        <Divider />
      </>
    )
  }

  const renderBody = () => {
    return (
      <>
        <div className="u-flexbox u-justify-content-center">
          <Icon name={getIconName()} />
        </div>
        <Spacer size={Spacer.Size.Medium} />
        <Text as="span" text={getBodyText()} html />
      </>
    )
  }

  const renderActions = () => {
    return (
      <>
        <Spacer size={Spacer.Size.X2Large} />
        <Button
          text={getActionText()}
          styling={Button.Styling.Filled}
          url={getActionUrl()}
          onClick={handleClick}
          testId="badge-info-modal-upload-item"
        />
      </>
    )
  }

  return (
    <Dialog
      show={!!selectedBadge}
      closeOnOverlay
      defaultCallback={handleOnClose}
      hasScrollableContent
    >
      <div>
        {renderHeader()}
        <ScrollableArea>
          <Cell>
            {renderBody()}
            {renderActions()}
          </Cell>
        </ScrollableArea>
      </div>
    </Dialog>
  )
}

export default BadgeInfoModal
